import clsx from 'clsx'
import { Link, type Href } from 'expo-router'
import type { PropsWithChildren, ReactNode } from 'react'
import { Pressable, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card } from '@/src/elements/card'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'

import { message } from './message-variants'
import type { TMessageProps } from './Message.types'

const LinkAsChildWrapper = (props: { children: ReactNode; hasLink: boolean }) => {
  const { hasLink, children } = props
  if (hasLink) return <Pressable {...props} />
  return children
}

export const Message = ({
  colors = 'secondary',
  iconName,
  buttons,
  onClose,
  children,
  className,
  testID,
  link,
  textClassName,
}: PropsWithChildren<TMessageProps>): ReactNode => {
  const classnames = message({ colors })

  const getOptionalLinkProps = (href?: Href) =>
    href
      ? {
          as: Link,
          additionalProps: { href, role: 'link', asChild: true },
          isPressable: true,
          isHoverable: true,
          className: clsx(
            classnames,
            'web:transition-all web:duration-300 web:ease-in-out duration-300 ease-in-out',
            className,
          ),
        }
      : {
          className: clsx(classnames, className),
        }

  return (
    <Card {...getOptionalLinkProps(link)} shadow="none" testID={testID}>
      <LinkAsChildWrapper hasLink={link !== undefined}>
        {onClose != null && (
          <View className="absolute right-1 top-1 z-10 border-none">
            <Button
              testID={TEST_ID.MISCELLANEOUS.MESSAGE_CLOSE_BUTTON}
              isIconOnly
              startIcon={ICON.CLOSE}
              colors={colors}
              contrast="low"
              variant="light"
              size="xs"
              onPress={(e) => {
                e?.persist()
                e?.stopPropagation?.()
                e?.preventDefault?.()
                onClose()
              }}
            />
          </View>
        )}
        <View className={clsx('native:pr-4 flex flex-row items-start gap-3', textClassName)}>
          {iconName && (
            <Icon
              testID={TEST_ID.MISCELLANEOUS.MESSAGE_ICON}
              name={iconName}
              size="md" // REMINDER: size is always md here
              className={clsx(message.variants.colors[colors])}
            />
          )}
          <Typography className={clsx(message.variants.colors[colors], onClose ? 'pr-7' : '')}>{children}</Typography>
        </View>

        {buttons && (
          <View className="flex-row-reverse">
            <View className="max-w-full flex-row-reverse flex-wrap justify-center gap-2">
              {buttons.map((button, i) => (
                <Button
                  // oxlint-disable-next-line react/no-array-index-key -- no stable button id
                  key={button.id ?? button.testID ?? i}
                  colors={colors}
                  testID={TEST_ID.MISCELLANEOUS.MESSAGE_BUTTON}
                  variant="solid"
                  {...button}
                  onPress={(e) => {
                    e?.persist()
                    e?.stopPropagation?.()
                    e?.preventDefault?.()
                    button.onPress?.(e)
                  }}
                />
              ))}
            </View>
          </View>
        )}
      </LinkAsChildWrapper>
    </Card>
  )
}
